/**
 * Sales user — "My Zoho leads" page (G2.1).
 * All Zoho columns exposed. Uses normalized top-level fields,
 * falls back to _raw for extra fields (temperature, city, state, UTM, etc.)
 */
import { useEffect, useMemo, useState } from 'react';
import {
    RefreshCw, Search, AlertTriangle, Phone, Mail, ExternalLink,
    MapPin, Thermometer, Tag, ChevronDown, ChevronUp,
} from 'lucide-react';
import api from '../../utils/api';

const STATUS_CHIPS = {
    'Not Contacted': 'bg-amber-100 text-amber-800',
    'Contacted': 'bg-sky-100 text-sky-800',
    'Pre-Qualified': 'bg-violet-100 text-violet-800',
    'Qualified': 'bg-emerald-100 text-emerald-800',
    'Lost Lead': 'bg-rose-100 text-rose-800',
    'Junk Lead': 'bg-ink-200 text-ink-600',
    'Requested for meeting/demo': 'bg-blue-100 text-blue-800',
};

const TEMP_CHIPS = {
    'Hot': 'bg-red-100 text-red-700',
    'Warm': 'bg-orange-100 text-orange-700',
    'Cold': 'bg-blue-100 text-blue-700',
};

// All columns — toggle visibility with the column picker
const ALL_COLUMNS = [
    { key: 'name', label: 'Name', always: true },
    { key: 'company', label: 'Company' },
    { key: 'contact', label: 'Email / Phone' },
    { key: 'location', label: 'Location' },
    { key: 'source', label: 'Source' },
    { key: 'formType', label: 'Form Type' },
    { key: 'fbPage', label: 'FB Page' },
    { key: 'status', label: 'Status', always: true },
    { key: 'temperature', label: 'Temperature' },
    { key: 'tag', label: 'Tag' },
    { key: 'budget', label: 'Budget' },
    { key: 'decisionMaker', label: 'Decision Maker' },
    { key: 'preferredDay', label: 'Preferred Day' },
    { key: 'createdBy', label: 'Created By' },
    { key: 'modifiedBy', label: 'Modified By' },
    { key: 'utmSource', label: 'UTM Source' },
    { key: 'utmMedium', label: 'UTM Medium' },
    { key: 'utmCampaign', label: 'UTM Campaign' },
    { key: 'createdAt', label: 'Created' },
    { key: 'updatedAt', label: 'Updated' },
    { key: 'actions', label: '', always: true },
];

const DEFAULT_VISIBLE = new Set([
    'name', 'company', 'contact', 'location', 'source',
    'status', 'temperature', 'tag', 'createdAt', 'actions',
]);

export default function SalesLeads() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');
    const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE);
    const [showColPicker, setShowColPicker] = useState(false);

    const load = (fresh = false) => {
        setLoading(true);
        api.get('/zoho/my-leads', { params: { fresh: fresh ? 'true' : '' } })
            .then(r => setData(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(false); }, []);

    const leads = useMemo(() => {
        const rows = data?.leads || [];
        if (!q.trim()) return rows;
        const rx = new RegExp(q.trim(), 'i');
        return rows.filter(l => {
            const r = l._raw || {};
            const blob = [
                l.firstName, l.lastName, l.fullName,
                l.email, l.phone,
                l.company, l.source, l.status, l.owner,
                l.city, l.state,
                r.Lead_Temperature, r.Tag,
                r.Facebook_Page_Name, r.Form_Type,
                r.UTM_Source, r.UTM_Medium, r.UTM_Campaign,
                r.Created_By, r.Modified_By,
            ].join(' ');
            return rx.test(blob);
        });
    }, [data, q]);

    const toggleCol = (key) => {
        setVisibleCols(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const col = (key) => visibleCols.has(key);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold">My Zoho leads</h1>
                    <p className="text-sm text-ink-500">
                        Leads where you're the owner in Zoho CRM. Synced on demand — data is at most 5 minutes old.
                    </p>
                </div>
                <div className="flex gap-2">
                    <div className="relative">
                        <button
                            onClick={() => setShowColPicker(v => !v)}
                            className="ag-btn-ghost text-xs"
                        >
                            Columns {showColPicker ? <ChevronUp className="h-3 w-3 inline" /> : <ChevronDown className="h-3 w-3 inline" />}
                        </button>
                        {showColPicker && (
                            <div className="absolute right-0 top-9 z-50 bg-white border border-ink-200 rounded-xl shadow-lg p-3 w-56 grid grid-cols-2 gap-1">
                                {ALL_COLUMNS.filter(c => !c.always).map(c => (
                                    <label key={c.key} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={visibleCols.has(c.key)}
                                            onChange={() => toggleCol(c.key)}
                                            className="rounded"
                                        />
                                        {c.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <button onClick={() => load(true)} className="ag-btn-ghost" disabled={loading}>
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                </div>
            </div>

            {/* No owner keys warning */}
            {data && data.scoped === 'no-owner-keys' && (
                <div className="ag-card p-6 bg-amber-50 ring-1 ring-amber-200 text-sm">
                    <div className="inline-flex items-start gap-2 text-amber-800">
                        <AlertTriangle className="h-4 w-4 mt-0.5" />
                        <div>
                            <b>Your account isn't linked to a Zoho owner yet.</b>
                            <p className="text-[12px] mt-1">{data.hint}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {data && (data.leads || []).length === 0 && !loading && (
                <div className="ag-card p-8 text-center text-sm text-ink-500">
                    No leads here yet. As soon as someone assigns a Zoho lead to your email or Zoho user ID,
                    it'll appear here within 5 minutes.
                </div>
            )}

            {/* Search */}
            {data && (data.leads || []).length > 0 && (
                <>
                    <div className="ag-card p-3 flex items-center gap-2">
                        <Search className="h-4 w-4 text-ink-500" />
                        <input
                            className="ag-input flex-1"
                            placeholder="Search name, phone, email, company, tag, UTM…"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                        />
                        <span className="text-xs text-ink-500">{leads.length} of {data.leads.length}</span>
                        {data.cached && (
                            <span className="text-[10px] text-ink-500 italic">
                                cached {Math.round((data.age || 0) / 1000)}s ago
                            </span>
                        )}
                    </div>

                    <div className="ag-card p-0 overflow-x-auto">
                        <table className="ag-table">
                            <thead>
                                <tr>
                                    {col('name') && <th>Name</th>}
                                    {col('company') && <th>Company</th>}
                                    {col('contact') && <th>Email / Phone</th>}
                                    {col('location') && <th>Location</th>}
                                    {col('source') && <th>Source</th>}
                                    {col('formType') && <th>Form Type</th>}
                                    {col('fbPage') && <th>FB Page</th>}
                                    {col('status') && <th>Status</th>}
                                    {col('temperature') && <th>Temp</th>}
                                    {col('tag') && <th>Tag</th>}
                                    {col('budget') && <th>Budget</th>}
                                    {col('decisionMaker') && <th>Decision Maker</th>}
                                    {col('preferredDay') && <th>Preferred Day</th>}
                                    {col('createdBy') && <th>Created By</th>}
                                    {col('modifiedBy') && <th>Modified By</th>}
                                    {col('utmSource') && <th>UTM Source</th>}
                                    {col('utmMedium') && <th>UTM Medium</th>}
                                    {col('utmCampaign') && <th>UTM Campaign</th>}
                                    {col('createdAt') && <th>Created</th>}
                                    {col('updatedAt') && <th>Updated</th>}
                                    {col('actions') && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map(l => {
                                    const r = l._raw || {};
                                    return (
                                        <tr key={l.id}>
                                            {col('name') && (
                                                <td className="font-medium whitespace-nowrap">
                                                    {l.fullName || ((l.firstName || '') + ' ' + (l.lastName || '')).trim() || '—'}
                                                </td>
                                            )}
                                            {col('company') && (
                                                <td className="text-xs">{l.company || r.Company || '—'}</td>
                                            )}
                                            {col('contact') && (
                                                <td>
                                                    {l.email && (
                                                        <div className="text-xs inline-flex items-center gap-1 text-ink-700">
                                                            <Mail className="h-3 w-3 text-ink-500" /> {l.email}
                                                        </div>
                                                    )}
                                                    {l.phone && (
                                                        <div className="text-[11px] inline-flex items-center gap-1 text-ink-500 mt-0.5">
                                                            <Phone className="h-3 w-3" /> {l.phone}
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                            {col('location') && (
                                                <td className="text-xs">
                                                    {(l.city || l.state)
                                                        ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-ink-400" />{[l.city, l.state].filter(Boolean).join(', ')}</span>
                                                        : '—'}
                                                </td>
                                            )}
                                            {col('source') && <td className="text-xs">{l.source || '—'}</td>}
                                            {col('formType') && <td className="text-xs">{r.Form_Type || '—'}</td>}
                                            {col('fbPage') && <td className="text-xs">{r.Facebook_Page_Name || '—'}</td>}
                                            {col('status') && (
                                                <td>
                                                    <span className={`ag-chip ${STATUS_CHIPS[l.status] || 'bg-ink-100 text-ink-700'}`}>
                                                        {l.status || '—'}
                                                    </span>
                                                </td>
                                            )}
                                            {col('temperature') && (
                                                <td>
                                                    {r.Lead_Temperature
                                                        ? <span className={`ag-chip ${TEMP_CHIPS[r.Lead_Temperature] || 'bg-ink-100 text-ink-700'}`}>{r.Lead_Temperature}</span>
                                                        : '—'}
                                                </td>
                                            )}
                                            {col('tag') && <td className="text-xs">{r.Tag || '—'}</td>}
                                            {col('budget') && <td className="text-xs">{r.Daewoo_Investment_Budget1 || '—'}</td>}
                                            {col('decisionMaker') && <td className="text-xs">{r.Who_will_be_the_decision_maker || '—'}</td>}
                                            {col('preferredDay') && <td className="text-xs">{r.Preferred_day || '—'}</td>}
                                            {col('createdBy') && <td className="text-xs">{r.Created_By || '—'}</td>}
                                            {col('modifiedBy') && <td className="text-xs">{r.Modified_By || '—'}</td>}
                                            {col('utmSource') && <td className="text-xs">{r.UTM_Source || '—'}</td>}
                                            {col('utmMedium') && <td className="text-xs">{r.UTM_Medium || '—'}</td>}
                                            {col('utmCampaign') && <td className="text-xs max-w-[160px] truncate" title={r.UTM_Campaign}>{r.UTM_Campaign || '—'}</td>}
                                            {col('createdAt') && <td className="text-xs text-ink-500 whitespace-nowrap">{l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN') : '—'}</td>}
                                            {col('updatedAt') && <td className="text-xs text-ink-500 whitespace-nowrap">{l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('en-IN') : '—'}</td>}
                                            {col('actions') && (
                                                <td className="text-right">
                                                    <a
                                                        href={`https://crm.zoho.in/crm/EntityInfo.do?module=Leads&id=${l.id}`}
                                                        target="_blank" rel="noreferrer"
                                                        className="ag-btn-ghost text-xs"
                                                        title="Open in Zoho"
                                                    >
                                                        Zoho <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}